// SEO-12 · /resources/what-deleting-student-data-means
//
// EDITORIAL GROUNDING (see docs/marketing/RESOURCES_EDITORIAL_PROCESS.md):
//   • Erasure is redact-in-place, not row deletion: `core/dpdp.erase_student` keeps id/class/
//     section/status so the financial ledger stays coherent, clears name/guardians/address/photo/
//     dob/aadhaar_last4/apaar_id/udise_pen/blood_group, redacts the denormalised student_name on
//     invoices/payments/transport/hostel/library/DigiLocker, hard-deletes notifications, messages,
//     consent tokens and the health/infirmary/immunisation records, soft-deletes uploaded files,
//     and severs task links.
//   • `core/dpdp.export_student` assembles the access/portability copy across collections.
//   • Retention: `core/retention.py` AUTO_PURGE is a narrow allowlist (notifications, messages,
//     consent tokens); leads are ANONYMISED in place; everything else is retained and removed only
//     through the erasure path. The sweep is gated by RETENTION_PURGE_ENABLED.
//   • Retention windows + data classes are declared once in `core/data_classification.py`
//     (docs/compliance/RETENTION_POLICY.md).
//   • Registry S5 wording — supporting controls, staged DPDP commencement, not legal advice.
// NO legal advice, no claim that erasure discharges a statutory duty.

const article = {
  slug: "what-deleting-student-data-means",
  title: "What \"delete a student's data\" can and cannot mean",
  seoTitle: "Deleting Student Data: What It Means | Vidyapeeth360",
  description:
    "A family asks a school to delete their child's data. What can honestly be deleted, what must be kept, and how to do both without breaking the ledger.",
  topic: "Compliance",
  topicKey: "compliance",
  readingMinutes: 6,
  published: "2026-08-18",
  updated: "2026-08-18",
  standfirst:
    "\"Delete everything\" is a reasonable thing for a parent to ask and an impossible thing for a school to do literally. The honest answer is more useful than the comfortable one — and it can be given in a single page.",
  moduleLinks: ["document-management", "role-and-permission", "fee-reports"],
  conversion: {
    to: "/contact",
    label: "Ask what erasure removes on your records",
    note: "We will show the fields that clear, the records that survive and why — on your data model, not a slide.",
  },
  sections: [
    {
      id: "the-collision",
      heading: "Two duties that point in opposite directions",
      blocks: [
        {
          t: "p",
          text: "A school holds a child's data because it had to. Then a family leaves and asks for it to be removed. The request is legitimate. So is the school's obligation to keep books, examination records and audit trails for years after the child has gone.",
        },
        {
          t: "p",
          text: "Both are real, and the resolution is not a compromise. It is a distinction: erase the person from the record, keep the record. A receipt for ₹42,000 has to survive an audit; the child's name, address and guardian's phone number do not have to survive on it.",
        },
      ],
    },
    {
      id: "three-outcomes",
      heading: "Three different things people call deletion",
      blocks: [
        {
          t: "ul",
          items: [
            "**Removal** — the row is gone. Appropriate for things that exist only to communicate: notifications, message logs, one-time tokens. Nothing later depends on them.",
            "**Redaction in place** — the row survives, the person does not. The invoice keeps its number, date and amount; the name becomes a placeholder. This is what makes an audited ledger and a family's request compatible.",
            "**Anonymisation** — the record is kept for aggregate purposes with the identifiers scrubbed. Useful for enquiry and pipeline data, where the count matters and the individual does not.",
          ],
        },
        {
          t: "p",
          text: "Most \"delete the data\" conversations go wrong because the two sides mean different items from that list. Say which one you mean, per record type, in writing, and the conversation becomes short.",
        },
      ],
    },
    {
      id: "what-must-stay",
      heading: "What must stay, and how to justify it",
      blocks: [
        {
          t: "p",
          text: "Before deciding anything, write down which categories your school holds and how long each is kept. Not per file — per category. That list is the thing that lets you answer a parent in one sentence instead of a week.",
        },
        {
          t: "ol",
          items: [
            "**Money.** Invoices, receipts, refunds, ledger postings. Retained for whatever period your own accounting and tax obligations set — confirm that period with your auditor and write it down; the personal identifiers on them can go.",
            "**Academic outcomes.** Marks and results a board, another school or a university may need to verify. Retained; contact details attached to them are not the outcome.",
            "**Audit trails.** Who did what, when. These exist precisely so they cannot be edited after the fact — a system that lets you delete its own audit log has no audit log.",
            "**Special-category records.** Health, infirmary, counselling and wellbeing entries about a child. These are the most sensitive thing a school holds, and unlike the ledger there is rarely a reason to keep them once the child has left. Treat them as removable, and check that your system actually removes them rather than leaving a parallel copy behind.",
          ],
        },
      ],
    },
    {
      id: "denormalised-copies",
      heading: "The copies nobody remembers",
      blocks: [
        {
          t: "p",
          text: "The hard part of erasure is not the student record. It is the eleven other places the child's name was copied for convenience — the transport assignment, the hostel gate pass, the library loan, the delivery log for a message, the uploaded birth certificate, the task somebody created about them.",
        },
        {
          t: "p",
          text: "Each of those is a genuine operational record that may need to survive. What must not survive is the name and the guardian's phone number sitting on it, because that is exactly what makes an \"erased\" student recoverable by searching for their name. Ask your vendor, specifically: which records keep a copy of the student's name, and what happens to each one on erasure? A confident \"we delete everything\" is usually a system that has never enumerated the list.",
        },
        {
          t: "note",
          title: "Erasure is not the same as leaving",
          text: "A student who transfers out should be marked inactive and retained. Erasure is a separate, requested, recorded action. Running erasure automatically on every leaver destroys records you are obliged to keep — and it is not what the family asked for either.",
        },
      ],
    },
    {
      id: "in-the-product",
      heading: "How this works in Vidyapeeth360",
      blocks: [
        {
          t: "p",
          text: "Erasure is redact-in-place. The student row survives with its identifier, class, section and status so the financial ledger and audit trail stay coherent, and the personal fields are cleared: name, guardians, address, photograph, date of birth, blood group, and the national identifiers including the Aadhaar reference, APAAR ID and UDISE PEN. The record is marked erased with the time it happened.",
        },
        {
          t: "p",
          text: "The copies are enumerated rather than hoped about. Invoices and payments keep the row and lose the name. Transport assignments, hostel allocations and gate passes, library loans and the DigiLocker push record are each stripped of the denormalised name and guardian phone; uploaded documents are withdrawn; task records keep the operational history but lose the link back to the child. Notifications, message logs, consent tokens and the health, infirmary and immunisation records are removed outright — special-category data about a child should not linger because it lived in a different table.",
        },
        {
          t: "p",
          text: "Retention is deliberately conservative and separate from erasure. Windows are declared once per collection, and only a narrow allowlist of low-risk operational records is ever purged automatically — enquiry records are anonymised in place rather than deleted, and financial, student, audit and consent data is never auto-deleted at all. The automatic sweep is off unless a deployment turns it on.",
        },
        {
          t: "p",
          text: "Before erasing, export. The access copy assembles the student's record across the register, invoices, payments, refunds, attendance, marks, library, hostel, transport, consent and communications, so the family can be given what the school held. Erasure after that is a decision somebody made and can point at — not a delete key. Who may run either is a permission question, which is why the [access model](/modules/role-and-permission) matters as much as the mechanism, and why the surviving [fee record](/modules/fee-reports) and the withdrawn [documents](/modules/document-management) end up in different states.",
        },
        {
          t: "p",
          text: "This is a set of controls, not a compliance certificate. Your school still owns the notices, the lawful basis, the vendor list and the decision itself — the product's contribution is that the decision is executed consistently and leaves a trail.",
        },
      ],
    },
  ],
  // The audit's structural finding: a compliance article must render a real "Primary sources"
  // block, citing the SAME vetted primary sources the product already cites on /compliance/dpdp
  // (core/knowledge.py "dpdp_rulebook") rather than paraphrasing the law into a claim of ours.
  sources: [
    {
      label: "Digital Personal Data Protection Act, 2023 (MeitY)",
      href: "https://www.meity.gov.in/static/uploads/2024/02/Digital-Personal-Data-Protection-Act-2023.pdf",
      note: "The Act as published by the Ministry of Electronics and Information Technology.",
    },
    {
      label: "Digital Personal Data Protection Rules, 2025 (MeitY)",
      href: "https://www.meity.gov.in/static/uploads/2025/11/53450e6e5dc0bfa85ebd78686cadad39.pdf",
      note: "The final Rules, including their staged commencement schedule.",
    },
  ],
};

export default article;
